"use client";

import { useTranslation } from "react-i18next";

/**
 * 2026-04-30 — Shimmer-block skeleton primitives.
 *
 * Small, dependency-free shimmer used as the loading placeholder for
 * pages that fetch user data on mount (Polymarket / Robinhood do this
 * pattern on /home, /portfolio, /markets/[slug]). Replaces the bare
 * "Loading…" text so the layout doesn't jump when the real content
 * lands.
 *
 * Tailwind's animate-pulse is fine for the closed-beta volume; once
 * we hit Gate B traffic we can graduate to a CSS gradient shimmer if
 * the pulse looks cheap.
 *
 * Usage:
 *   <Skeleton className="h-4 w-32" />
 *   <SkeletonHomeDashboard />     // composed shape for /home pre-fetch
 */
export function Skeleton({ className = "" }: { className?: string }) {
  return (
    <span
      aria-hidden="true"
      className={`block animate-pulse rounded bg-[var(--color-surface2)] ${className}`}
    />
  );
}

export function SkeletonHomeDashboard() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t("skeleton.loadingDashboard")}
      className="space-y-5"
    >
      {/* Ticker strip */}
      <Skeleton className="h-9 w-full rounded-md" />

      {/* Profile + PnL row — two cards on lg, stacked otherwise */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        <SkeletonCard rows={3} />
        <SkeletonCard rows={3} />
      </div>

      {/* Tabs row + sub-filter row */}
      <div className="space-y-3 border-b border-[var(--color-border)] pb-3">
        <div className="flex gap-5">
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
          <Skeleton className="h-5 w-20" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-8 w-32 rounded-md" />
          <Skeleton className="h-8 flex-1 rounded-md" />
          <Skeleton className="h-8 w-32 rounded-md" />
        </div>
      </div>

      {/* Three positions rows */}
      <div className="space-y-2">
        <SkeletonRow />
        <SkeletonRow />
        <SkeletonRow />
      </div>
    </div>
  );
}

function SkeletonCard({ rows }: { rows: number }) {
  return (
    <div className="rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4">
      <Skeleton className="mb-3 h-5 w-32" />
      <div className="space-y-2">
        {Array.from({ length: rows }, (_, i) => (
          <Skeleton key={i} className="h-4 w-full" />
        ))}
      </div>
    </div>
  );
}

function SkeletonRow() {
  return (
    <div className="flex items-center gap-3 rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5">
      <Skeleton className="h-4 flex-1" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-16" />
      <Skeleton className="h-4 w-20" />
    </div>
  );
}

export function SkeletonRewards() {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-label={t("skeleton.loadingRewards")}
      className="space-y-5"
    >
      {/* Three summary tiles */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <SkeletonTile />
        <SkeletonTile />
        <SkeletonTile />
      </div>
      {/* Per-market list (3 rows) */}
      <div className="space-y-2">
        <Skeleton className="h-3 w-24" />
        <div className="space-y-2">
          <SkeletonListRow />
          <SkeletonListRow />
          <SkeletonListRow />
        </div>
      </div>
    </div>
  );
}

function SkeletonTile() {
  return (
    <div className="rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] p-3">
      <Skeleton className="mb-2 h-3 w-20" />
      <Skeleton className="h-5 w-24" />
    </div>
  );
}

function SkeletonListRow() {
  return (
    <div className="flex items-center justify-between rounded-md border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2">
      <Skeleton className="h-4 w-40" />
      <Skeleton className="h-4 w-16" />
    </div>
  );
}
